import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Select, Spin, Row, Col, Empty, Switch, Button, message } from 'antd';
import { FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AYSService from '../../services/AYSService';

const { Option } = Select;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// QoC rating bands - same scale as the QoC assessment screens
const BANDS = [
    { min: 98, label: 'Outstanding',          range: '98 - 100%',  color: '#389e0d', text: '#fff' },
    { min: 95, label: 'Excellent',            range: '95 - 97.9%', color: '#1890ff', text: '#fff' },
    { min: 90, label: 'Good',                 range: '90 - 94.9%', color: '#52c41a', text: '#fff' },
    { min: 85, label: 'Fair',                 range: '85 - 89.9%', color: '#faad14', text: '#000' },
    { min: 70, label: 'Requires Improvement', range: '70 - 84.9%', color: '#fa8c16', text: '#fff' },
    { min: 0,  label: 'Inadequate',           range: 'Below 70%',  color: '#f5222d', text: '#fff' },
];
const isNum = v => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const bandFor = pct => (isNum(pct) ? (BANDS.find(b => Number(pct) >= b.min) || BANDS[BANDS.length - 1]) : null);
const colorFor = pct => { const b = bandFor(pct); return b ? b.color : '#d9d9d9'; };
const shorten = (s, n) => (s && s.length > n ? s.substring(0, n - 3) + '...' : (s || ''));
// Short label for chart axes: use the saved short_label, otherwise shorten the question text automatically
const autoShort = (text, max = 28) => {
    let s = String(text || '').replace(/\s*\([^)]*\)/g, '').trim();
    s = s.split(/[.;:]/)[0].trim();
    s = s.replace(/^(the\s+)?(health\s+(care\s+)?providers?|facility)\s+/i, '');
    s = s.replace(/\s+/g, ' ');
    if (s.length > max) {
        const cut = s.substring(0, max - 3);
        const sp = cut.lastIndexOf(' ');
        s = (sp > 12 ? cut.substring(0, sp) : cut) + '...';
    }
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};
const labelFor = q => ((q.short_label && String(q.short_label).trim()) ? String(q.short_label).trim() : autoShort(q.question_text));
const download = (href, name) => {
    const a = document.createElement('a');
    a.href = href; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

export default function QoCAYSDashboard() {
    const exportRef = useRef(null);
    const [enrolled, setEnrolled]                 = useState([]);
    const [region, setRegion]                     = useState(null);
    const [facilityId, setFacilityId]             = useState(null);
    const [year, setYear]                         = useState(null);
    const [month, setMonth]                       = useState(null);
    const [includeSubmitted, setIncludeSubmitted] = useState(false);
    const [data, setData]                         = useState(null);
    const [loading, setLoading]                   = useState(false);
    const [exporting, setExporting]               = useState(false);

    useEffect(() => {
        AYSService.getFacilities()
            .then(r => setEnrolled(r.data?.data || []))
            .catch(() => {});
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (region)           params.region = region;
        if (facilityId)       params.facility_id = facilityId;
        if (year)             params.year = year;
        if (month)            params.month = month;
        if (includeSubmitted) params.include_submitted = 1;
        AYSService.getDashboard(params)
            .then(r => setData(r.data?.data || null))
            .catch(e => message.error(e.message))
            .finally(() => setLoading(false));
    }, [region, facilityId, year, month, includeSubmitted]);

    useEffect(() => { load(); }, [load]);

    const years   = data?.years || [];
    const compare = !year && years.length > 1;
    const summary = data?.summary || {};
    const overallBand = bandFor(summary.overall_pct);
    const empty = data && !summary.total_scorecards;

    const allFacilities = (data && data.facilities && data.facilities.length)
        ? data.facilities
        : enrolled.map(f => ({ id: f.facility_id || f.id, name: f.facility_name || f.business_name, region: f.located_region || f.region || null }));
    const regions      = Array.from(new Set(allFacilities.map(f => f.region).filter(Boolean))).sort();
    const facilityList = region ? allFacilities.filter(f => f.region === region) : allFacilities;

    const facObj       = allFacilities.find(f => f.id === facilityId);
    const facilityName = facObj ? facObj.name : null;
    const regionName   = region || (facObj && facObj.region) || null;
    const basisText    = includeSubmitted ? 'Approved and submitted scorecards' : 'Approved scorecards';

    const headerTitle = facilityName
        || (region ? region + ' Region' : 'National (all facilities)');
    const headerSub = [
        (facilityName && regionName) ? regionName + ' Region' : null,
        year || 'All years',
        month ? MONTHS[month - 1] : 'All months',
        basisText,
        'Generated ' + new Date().toLocaleDateString(),
    ].filter(Boolean).join('  |  ');

    const fileName = () => 'AYS_Dashboard_'
        + (facilityName ? facilityName.replace(/[^A-Za-z0-9]+/g, '_') : (region ? region.replace(/[^A-Za-z0-9]+/g, '_') : 'National'))
        + '_' + (year || 'AllYears') + (month ? '_' + MONTHS[month - 1] : '');

    const capture = () => html2canvas(exportRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });

    const exportPng = async () => {
        setExporting(true);
        try {
            const canvas = await capture();
            download(canvas.toDataURL('image/png'), fileName() + '.png');
        } catch (e) { message.error('Export failed: ' + e.message); }
        setExporting(false);
    };

    const exportPdf = async () => {
        setExporting(true);
        try {
            const canvas = await capture();
            const w = canvas.width / 2;
            const h = canvas.height / 2;
            const pdf = new jsPDF({ orientation: w > h ? 'landscape' : 'portrait', unit: 'px', format: [w, h], hotfixes: ['px_scaling'] });
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
            pdf.save(fileName() + '.pdf');
        } catch (e) { message.error('Export failed: ' + e.message); }
        setExporting(false);
    };

    const onRegion = v => { setRegion(v || null); setFacilityId(null); };

    const themeOption = (theme) => {
        const qs = theme.questions || [];
        const labels = qs.map(q => labelFor(q));
        const yrs = compare ? years : [null];
        const series = yrs.map((yr, si) => ({
            name: yr ? String(yr) : 'Score',
            type: 'bar',
            barMaxWidth: 14,
            data: qs.map(q => {
                const raw = yr ? (q.year_pct ? q.year_pct[yr] : null) : q.pct;
                const val = isNum(raw) ? Number(raw) : null;
                return {
                    value: val === null ? 0 : val,
                    noData: val === null,
                    label: (val === null && !compare) ? { show: true, position: 'right', formatter: 'No data', color: '#999', fontSize: 10, fontStyle: 'italic' } : undefined,
                    itemStyle: { color: colorFor(val), opacity: compare ? 0.45 + 0.55 * (si + 1) / yrs.length : 1 },
                };
            }),
            label: {
                show: true, position: 'right', fontSize: 10,
                formatter: p => ((isNum(p.value) && !(p.data && p.data.noData)) ? Math.round(p.value) + '%' : ''),
            },
        }));
        return {
            animation: false,
            grid: { left: 8, right: 44, top: 10, bottom: 46, containLabel: true },
            tooltip: {
                trigger: 'item', confine: true,
                formatter: p => {
                    const q = qs[p.dataIndex] || {};
                    const b = bandFor(p.value);
                    return '<div style="max-width:260px;white-space:normal">' + (q.question_text || '') + '</div><b>'
                        + p.seriesName + ': ' + (isNum(p.value) && !(p.data && p.data.noData) ? p.value + '% (' + b.label + ')' : 'No data') + '</b>';
                },
            },
            xAxis: {
                type: 'value', min: 0, max: 100, interval: 25,
                name: 'Score (%)', nameLocation: 'middle', nameGap: 30,
                nameTextStyle: { fontSize: 11, fontWeight: 'bold' },
                axisLabel: { fontSize: 10, formatter: '{value}%' },
            },
            yAxis: {
                type: 'category', inverse: true, data: labels,
                axisLabel: { fontSize: 10, width: 150, overflow: 'break', lineHeight: 12 },
            },
            series,
        };
    };

    const trendOption = () => {
        const rows = data?.trend || [];
        const bands = [[25, 70, '#f5222d'], [70, 85, '#fa8c16'], [85, 90, '#faad14'], [90, 95, '#52c41a'], [95, 98, '#1890ff'], [98, 100, '#389e0d']];
        return {
            animation: false,
            grid: { left: 62, right: 24, top: 24, bottom: 52 },
            tooltip: {
                trigger: 'axis',
                formatter: ps => {
                    const r = rows[ps[0].dataIndex] || {};
                    const b = bandFor(r.pct);
                    return MONTHS[r.mo - 1] + ' ' + r.yr + '<br/>Score: ' + r.pct + '%' + (b ? ' (' + b.label + ')' : '')
                        + '<br/>Mean: ' + r.mean_score + ' / 4<br/>Scorecards: ' + r.scorecards;
                },
            },
            xAxis: {
                type: 'category', data: rows.map(r => MONTHS[r.mo - 1] + ' ' + r.yr),
                name: 'Month', nameLocation: 'middle', nameGap: 30,
                nameTextStyle: { fontSize: 12, fontWeight: 'bold' },
            },
            yAxis: {
                type: 'value', min: 25, max: 100,
                name: 'Overall score (%)', nameLocation: 'middle', nameGap: 44,
                nameTextStyle: { fontSize: 12, fontWeight: 'bold' },
                axisLabel: { formatter: '{value}%' },
            },
            series: [{
                type: 'line', symbolSize: 9,
                lineStyle: { color: '#1e3a5f', width: 2 },
                data: rows.map(r => ({ value: isNum(r.pct) ? Number(r.pct) : null, itemStyle: { color: colorFor(r.pct) } })),
                label: { show: true, position: 'top', fontSize: 11, formatter: p => (isNum(p.value) ? Math.round(p.value) + '%' : '') },
                markArea: {
                    silent: true,
                    data: bands.map(b => [{ yAxis: b[0], itemStyle: { color: b[2], opacity: 0.10 } }, { yAxis: b[1] }]),
                },
            }],
        };
    };

    const filters = (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <Select allowClear placeholder="All regions" style={{ width: 150 }} value={region} onChange={onRegion}>
                {regions.map(r => <Option key={r} value={r}>{r}</Option>)}
            </Select>
            <Select showSearch allowClear placeholder="All facilities" style={{ width: 260 }}
                value={facilityId} onChange={v => setFacilityId(v || null)} optionFilterProp="children">
                {facilityList.map(f => <Option key={f.id} value={f.id}>{f.name}</Option>)}
            </Select>
            <Select allowClear placeholder="All years" style={{ width: 110 }} value={year} onChange={setYear}>
                {years.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Select allowClear placeholder="All months" style={{ width: 120 }} value={month} onChange={setMonth}>
                {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
            <span style={{ fontSize: 12, color: '#555' }}>
                <Switch size="small" checked={includeSubmitted} onChange={setIncludeSubmitted} /> Include submitted (not yet approved)
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Button icon={<FileImageOutlined />} loading={exporting} disabled={!data || empty} onClick={exportPng}>Export PNG</Button>
                <Button icon={<FilePdfOutlined />} loading={exporting} disabled={!data || empty} onClick={exportPdf}>Export PDF</Button>
            </span>
        </div>
    );

    const legend = (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, fontSize: 11 }}>
            {BANDS.map(b => (
                <span key={b.label} style={{ background: b.color, color: b.text, padding: '2px 10px', borderRadius: 3 }}>
                    {b.label} {b.range}
                </span>
            ))}
        </div>
    );

    return (
        <div>
            {filters}
            <Spin spinning={loading}>
                <div ref={exportRef} style={{ background: '#fff', padding: 8 }}>
                    <div style={{ padding: '2px 0 10px', borderBottom: '3px solid #0f5c5c', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f5c5c', letterSpacing: 0.5 }}>AYS CLIENT SATISFACTION SCORECARD</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e3a5f', lineHeight: 1.3 }}>{headerTitle}</div>
                        <div style={{ fontSize: 12, color: '#555' }}>{headerSub}</div>
                    </div>
                    {legend}
                    {empty && (
                        <Empty description={'No ' + (includeSubmitted ? 'approved or submitted' : 'approved') + ' scorecards match these filters'} />
                    )}
                    {data && !empty && (
                        <div>
                            <Row gutter={12} style={{ marginBottom: 12 }}>
                                <Col xs={24} md={12}>
                                    <Card size="small" style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#888', fontSize: 12 }}>Total scorecards</div>
                                        <div style={{ fontSize: 34, fontWeight: 800, color: '#1e3a5f' }}>{summary.total_scorecards}</div>
                                        <div style={{ color: '#888', fontSize: 12 }}>{summary.total_facilities} facilit{summary.total_facilities === 1 ? 'y' : 'ies'}</div>
                                    </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Card size="small" style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#888', fontSize: 12 }}>Overall score</div>
                                        <div style={{ fontSize: 34, fontWeight: 800, color: overallBand ? overallBand.color : '#888' }}>
                                            {isNum(summary.overall_pct) ? Math.round(summary.overall_pct) + '%' : 'n/a'}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                            {overallBand && (
                                                <span style={{ background: overallBand.color, color: overallBand.text, padding: '1px 10px', borderRadius: 3 }}>
                                                    {overallBand.label}
                                                </span>
                                            )}
                                            <span style={{ color: '#888', marginLeft: 8 }}>
                                                Mean {isNum(summary.overall_mean) ? Number(summary.overall_mean).toFixed(2) : 'n/a'} / 4
                                            </span>
                                        </div>
                                    </Card>
                                </Col>
                            </Row>

                            {compare && (
                                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                                    Year comparison: bars are coloured by QoC rating band; lighter bars are earlier years ({years.join(' then ')}).
                                </div>
                            )}

                            <Row gutter={[12, 12]}>
                                {(data.themes || []).map(t => {
                                    const b = bandFor(t.pct);
                                    const qn = (t.questions || []).length;
                                    const h = Math.max(140, qn * (compare ? years.length * 16 + 18 : 34) + 70);
                                    return (
                                        <Col key={t.theme} xs={24} lg={12} xl={8}>
                                            <div style={{ border: '1px solid #e8e8e8', background: '#fff' }}>
                                                <div style={{ background: '#0f5c5c', color: '#fff', padding: '6px 12px', display: 'flex',
                                                    justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 13 }}>
                                                    <span>{t.theme}</span>
                                                    <span style={{ background: b ? b.color : '#8c8c8c', color: b ? b.text : '#fff',
                                                        padding: '1px 10px', borderRadius: 10, fontSize: 12 }}>
                                                        {isNum(t.pct) ? Math.round(t.pct) + '%' : 'n/a'}
                                                    </span>
                                                </div>
                                                <div style={{ padding: 8 }}>
                                                    <ReactECharts option={themeOption(t)} style={{ height: h }} notMerge />
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>

                            <Card size="small" title="Overall monthly score trend" style={{ marginTop: 12 }}>
                                {(data.trend || []).length
                                    ? <ReactECharts option={trendOption()} style={{ height: 300 }} notMerge />
                                    : <Empty description="No monthly data" />}
                            </Card>
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
}